import "./Table.scss"

import React, { CSSProperties, FunctionComponent, MouseEvent, ReactNode, useState, useRef, useMemo, useEffect } from "react"
import { isEqual } from "lodash"
import cn from "classnames"
import { Scrollbars as CustomScrollbars } from "react-custom-scrollbars-2"

import { SortDirection } from "config/constants"
import { Clamp } from "@/types"
import TableHeaderCell from "./TableHeaderCell"
import useTable from "./useTable"
import HelpButton, { IHelpButtonProps } from "components/HelpButton/HelpButton"
import Scrollbars from "components/Scrollbars/Scrollbars"
import Flex from "components/Flex/Flex"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"

export type TableTheme = "border-between" | "border-all" | "no-border"
export type TableHeaderTheme = "grey" | "transparent"
export type TableRowHighlightStyle = "yellow" | "grey" | "red"

export type TableColumn = {
    title?: ReactNode | string
    explanation?: IHelpButtonProps["tooltipProps"]
    width?: CSSProperties['width']
    colSpan?: HTMLTableCellElement["colSpan"]
    sort?: SortDirection
    onSortChange?(sortDirection: SortDirection): Promise<void> | void
    minWidth?: CSSProperties['minWidth']
    maxWidth?: CSSProperties['maxWidth']
    className?: string
    headerClassName?: string
    bodyClassName?: string
}

export type TableRow = ReactNode[]

export interface ITableProps {
    columns: TableColumn[]
    rows: TableRow[]
    prependHeaderRow?: TableColumn[]
    theme?: TableTheme
    headerTheme?: TableHeaderTheme
    condensed?: boolean
    stickyHeader?: boolean
    clampHeaders?: Clamp
    isLoading?: boolean
    testId?: string
    highlightRowIndexes?: number[]
    highlightRowStyle?: TableRowHighlightStyle
    hoverHighlightRowStyle?: TableRowHighlightStyle
    freezeFirstColumn?: boolean
    freezeLastColumn?: boolean
    tableLayoutFixed?: boolean
    bordered?: boolean
    round?: boolean
    /** 
     * Enable advanced scrolling with grid layout, dual scrollbars for header/body sync, 
     * and perfect column alignment. Required for complex tables with wide content.
     */
    useAdvancedScrolling?: boolean
    /** 
     * Controls sticky header shadow in advanced scrolling mode. 
     * Should be true when parent container is scrolled vertically.
     */
    isPageScrolled?: boolean
    getRowKey?(index: number): string
    onRowClick?(indexOrKey: number | string, event: MouseEvent): void
}

const Table: FunctionComponent<React.PropsWithChildren<ITableProps>> = ({
    columns,
    rows,
    prependHeaderRow,
    theme = "border-all",
    headerTheme = "grey",
    condensed,
    stickyHeader,
    clampHeaders,
    isLoading,
    testId,
    highlightRowIndexes,
    highlightRowStyle,
    hoverHighlightRowStyle,
    freezeFirstColumn,
    freezeLastColumn,
    tableLayoutFixed,
    round,
    useAdvancedScrolling,
    isPageScrolled,
    getRowKey,
    onRowClick,
}) => {
    const withFreeze = freezeFirstColumn || freezeLastColumn

    const [horizontalScrollState, setHorizontalScrollState] = useState<{ scrolledToStart: boolean, scrolledToEnd: boolean }>({
        scrolledToStart: true,
        scrolledToEnd: false,
    })

    // Refs for advanced scrolling
    const tableHeaderScrollEl = useRef<CustomScrollbars>(null)
    const tableBodyScrollEl = useRef<CustomScrollbars>(null)
    const tableContainerRef = useRef<HTMLDivElement>(null)

    // Grid template columns for advanced scrolling mode
    const gridTemplateColumns = useMemo(() => {
        if (!useAdvancedScrolling) return ''
        return columns.map(col => {
            if (col.width) {
                return col.width
            } else if (col.maxWidth && col.minWidth) {
                return `minmax(${col.minWidth}, ${col.maxWidth})`
            } else if (col.minWidth && !col.maxWidth) {
                return `minmax(${col.minWidth}, 1fr)`
            } else if (col.maxWidth && !col.minWidth) {
                return `minmax(auto, ${col.maxWidth})`
            } else {
                return 'auto'
            }
        }).join(' ')
    }, [useAdvancedScrolling, columns])

    const {
        tableClassName,
        handleSortChange
    } = useTable({
        isLoading,
        theme,
        headerTheme,
        condensed,
        stickyHeader,
        withFreeze,
        horizontallyScrolledToStart: horizontalScrollState.scrolledToStart,
        horizontallyScrolledToEnd: horizontalScrollState.scrolledToEnd,
        round,
        useAdvancedScrolling
    })

    // Effect to ensure precise border alignment
    useEffect(() => {
        if (!useAdvancedScrolling || !freezeLastColumn) return

        // Get computed styles to ensure perfect alignment
        const syncBorderPositions = () => {
            const headerEl = tableContainerRef.current?.querySelector('.table__advanced-header')
            const bodyEl = tableContainerRef.current?.querySelector('.table__advanced-body')

            if (!headerEl || !bodyEl) return

            const headerLastFrozenCols = headerEl.querySelectorAll('.table__advanced-cell--last-column')
            const bodyLastFrozenCols = bodyEl.querySelectorAll('.table__advanced-cell--last-column')

            if (headerLastFrozenCols.length === 0 || bodyLastFrozenCols.length === 0) return

            // Apply the exact position to all frozen last columns
            headerLastFrozenCols.forEach((col) => {
                // Remove the default left border that might be causing doubling
                (col as HTMLElement).style.borderLeft = 'none';

                const borderEl = document.createElement('div')
                borderEl.className = 'table__cell--last-frozen-column-border'

                // Remove any existing border elements
                const existingBorders = col.querySelectorAll('.table__cell--last-frozen-column-border')
                existingBorders.forEach(b => b.remove())

                col.appendChild(borderEl)
                col.classList.add('position-relative')
            })

            bodyLastFrozenCols.forEach((col) => {
                // Remove the default left border that might be causing doubling
                (col as HTMLElement).style.borderLeft = 'none';

                const borderEl = document.createElement('div')
                borderEl.className = 'table__cell--last-frozen-column-border'

                // Remove any existing border elements
                const existingBorders = col.querySelectorAll('.table__cell--last-frozen-column-border')
                existingBorders.forEach(b => b.remove())

                col.appendChild(borderEl)
                col.classList.add('position-relative')
            })
        }

        // Run after render and on window resize
        syncBorderPositions()
        window.addEventListener('resize', syncBorderPositions)

        return () => {
            window.removeEventListener('resize', syncBorderPositions)
        }
    }, [useAdvancedScrolling, freezeLastColumn, columns, rows])

    const onScroll = (() => {
        let ticking = false

        return (e: React.UIEvent<HTMLElement>) => {
            if (!ticking) {
                window.requestAnimationFrame(function () {
                    const scrollLeft = (e.target as any).scrollLeft
                    const scrollWidth = (e.target as any).scrollWidth

                    const newHorizontalScrollState = {
                        scrolledToStart: scrollLeft === 0,
                        scrolledToEnd: scrollLeft + (e.target as any).clientWidth === scrollWidth,
                    }

                    if (!isEqual(horizontalScrollState, newHorizontalScrollState)) {
                        setHorizontalScrollState(newHorizontalScrollState)
                    }

                    ticking = false
                })
                ticking = true
            }
        }
    })()

    function renderHeaderRow(cols?: TableColumn[]) {
        if (!cols || cols.length === 0) return null
        return (
            <tr className="table__row table__row--header">
                {cols.map((col, colIndex) => (
                    <th
                        key={colIndex}
                        style={{ width: col.width, minWidth: col.minWidth, maxWidth: col.maxWidth }}
                        colSpan={col.colSpan}
                        className={cn([
                            "table__cell",
                            {
                                "table__cell--sortable": !!col.onSortChange,
                                "table__cell--freeze": (colIndex === 0 && freezeFirstColumn) || (colIndex === cols.length - 1 && freezeLastColumn)
                            },
                            col.className,
                            col.headerClassName,
                        ])}
                        onClick={col.onSortChange
                            ? () => handleSortChange(col)
                            : undefined
                        }>
                        <TableHeaderCell
                            col={col}
                            clamp={clampHeaders}
                        />
                    </th>
                ))}
            </tr>
        )
    }

    function renderAdvancedHeaderRow(cols?: TableColumn[]) {
        if (!cols || cols.length === 0) return null

        return cols.map((col, colIndex) => (
            <div
                key={colIndex}
                className={cn([
                    "table__cell table__cell--header px-2 py-1",
                    headerTheme === "grey" ? "bg-grey-100" : "",
                    {
                        "table__cell--sortable": !!col.onSortChange,
                        "table__cell--freeze": (colIndex === 0 && freezeFirstColumn) || (colIndex === cols.length - 1 && freezeLastColumn),
                        "table__advanced-cell--first-column": colIndex === 0 && freezeFirstColumn,
                        "table__advanced-cell--last-column": colIndex === cols.length - 1 && freezeLastColumn,
                        "table__advanced-cell--border-right": theme === "border-all" && colIndex !== cols.length - 1
                    },
                    col.headerClassName || "",
                ])}
                style={{
                    width: col.width,
                    minWidth: col.minWidth,
                    maxWidth: col.maxWidth,
                    height: condensed ? "4.8rem" : "5.6rem",
                }}
                onClick={col.onSortChange ? () => handleSortChange(col) : undefined}>
                <Flex alignItems="center" justifyContent="space-between" className="w-100 h-100">
                    <Flex alignItems="center" className="w-100">
                        <div className="table__head-label">
                            {typeof col.title === "string"
                                ? <FlexibleContent text={col.title} clamp={clampHeaders || 2} />
                                : col.title
                            }
                        </div>
                        {!!col.explanation &&
                            <div className="ml-1">
                                <HelpButton tooltipProps={col.explanation} />
                            </div>
                        }
                    </Flex>
                    {(col.sort || col.onSortChange) &&
                        <div className={`table__sort table__sort--${col.sort || "inactive"}`}></div>
                    }
                </Flex>
            </div>
        ))
    }

    if (useAdvancedScrolling) {
        return (
            <div
                ref={tableContainerRef}
                className={cn(
                    tableClassName,
                    "table--advanced-scrolling",
                    { "table--sticky-header-scrolled": isPageScrolled && stickyHeader }
                )}
                data-testid={testId}
            >
                {/* Header */}
                <div className="table__advanced-header" style={{ width: "100%", flexShrink: 0 }}>
                    <Scrollbars
                        ref={tableHeaderScrollEl}
                        style={{ width: "100%" }}
                        onScroll={e => tableBodyScrollEl.current?.scrollLeft(e.currentTarget.scrollLeft)}
                        renderView={({ style, ...props }) => <div style={{ ...style, display: "grid", gridTemplateColumns }} {...props} />}
                        thick>
                        {renderAdvancedHeaderRow(prependHeaderRow)}
                        {renderAdvancedHeaderRow(columns)}
                    </Scrollbars>
                </div>

                {/* Body */}
                <div className="table__advanced-body" style={{ width: "100%", flexShrink: 0 }}>
                    <Scrollbars
                        ref={tableBodyScrollEl}
                        style={{ width: "100%" }}
                        onScroll={e => tableHeaderScrollEl.current?.scrollLeft(e.currentTarget.scrollLeft)}
                        renderView={({ style, ...props }) => <div style={{ ...style, display: "grid", gridTemplateColumns }} {...props} />}
                        thick>
                        {rows.map((rowItems, rowIndex) =>
                            columns.map((col, colIndex) => (
                                <div
                                    key={`${getRowKey ? getRowKey(rowIndex) : rowIndex}-${colIndex}`}
                                    className={cn([
                                        "table__cell px-2 py-1",
                                        col.className,
                                        col.bodyClassName,
                                        {
                                            "table__cell--freeze": (colIndex === 0 && freezeFirstColumn) || (colIndex === columns.length - 1 && freezeLastColumn),
                                            "table__advanced-cell--first-column": colIndex === 0 && freezeFirstColumn,
                                            "table__advanced-cell--last-column": colIndex === columns.length - 1 && freezeLastColumn,
                                            "table__advanced-cell--border-right": theme === "border-all" && colIndex !== columns.length - 1,
                                            "table__advanced-cell--border-bottom": theme === "border-all" && rowIndex !== rows.length - 1,
                                            "table__advanced-cell--border-bottom-light": theme === "border-between" && rowIndex !== rows.length - 1,
                                        },
                                        highlightRowStyle && (highlightRowIndexes?.findIndex(i => i === rowIndex) ?? -1) > -1 ? `table__row--highlight-${highlightRowStyle}` : "",
                                        hoverHighlightRowStyle ? `table__row--hover-highlight-${hoverHighlightRowStyle}` : "",
                                    ])}
                                    style={{
                                        width: col.width,
                                        minWidth: col.minWidth,
                                        maxWidth: col.maxWidth,
                                    }}
                                    onClick={onRowClick ? e => onRowClick(getRowKey ? getRowKey(rowIndex) : rowIndex, e) : undefined}>
                                    {rowItems[colIndex]}
                                </div>
                            ))
                        ).flat()}
                    </Scrollbars>
                </div>
            </div>
        )
    }

    // Default table rendering (backward compatibility)
    return (
        <Scrollbars
            className={tableClassName}
            data-testid={testId}
            onScroll={withFreeze ? onScroll : undefined}>
            <table className={cn("table__table",
                { "table__table--layout-fixed": tableLayoutFixed }
            )}>
                <thead className="table__head">
                    {renderHeaderRow(prependHeaderRow)}
                    {renderHeaderRow(columns)}
                </thead>
                <tbody className="table__body">
                    {rows.map((rowItems, rowIndex) => (
                        <tr
                            key={getRowKey ? getRowKey(rowIndex) : rowIndex}
                            className={cn([
                                "table__row",
                                highlightRowStyle && (highlightRowIndexes?.findIndex(i => i === rowIndex) ?? -1) > -1 ? `table__row--highlight-${highlightRowStyle}` : "",
                                hoverHighlightRowStyle ? `table__row--hover-highlight-${hoverHighlightRowStyle}` : "",
                            ])}
                            onClick={onRowClick ? e => onRowClick(getRowKey ? getRowKey(rowIndex) : rowIndex, e) : undefined}
                            data-testid="table-row">
                            {columns.map((col, colIndex) => (
                                <td
                                    key={colIndex}
                                    style={{ width: col.width, minWidth: col.minWidth, maxWidth: col.maxWidth }}
                                    colSpan={col.colSpan}
                                    className={cn([
                                        "table__cell", col.className, col.bodyClassName,
                                        { "table__cell--freeze": (colIndex === 0 && freezeFirstColumn) || (colIndex === columns.length - 1 && freezeLastColumn) },
                                    ])}>
                                    {rowItems[colIndex]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </Scrollbars>
    )
}

export default Table
